/**
 * ModulesGrid — 1.422.0 Modules-tab concurrent tiling (P1 grid + P2 dock).
 *
 * Layout engine for open module tiles, generalizing TerminalGrid's
 * react-resizable-panels pattern (rows-of-2 chunking; see TerminalGrid.tsx —
 * deliberately NOT extracted into a shared helper to avoid touching the
 * terminals surface):
 *
 *   - maximized tile → that tile solo (others stay open in the store; the
 *     grid simply renders only the maximized one — remount cost is the
 *     lazy-bundle cache hit, and module React state lives in stores).
 *   - docked tile → outer PanelGroup on the dock axis: docked tile in its
 *     half, the remaining tiles chunked rows-of-2 in the other half.
 *   - default → rows-of-2 chunked grid with resize handles between rows and
 *     cells (autoSaveId persists panel sizes per browser).
 */
import { Fragment } from 'react';
import type { ReactNode } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import type { DockedModule } from '@/stores/uiStore';

const ROW_SIZE = 2;

function chunk<T>(arr: T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    rows.push(arr.slice(i, i + size));
  }
  return rows;
}

function RowHandle() {
  return (
    <PanelResizeHandle className="group relative flex items-center justify-center">
      <div className="h-1 w-full bg-vibe-border group-hover:bg-vibe-accent/50 group-active:bg-vibe-accent transition-colors" />
    </PanelResizeHandle>
  );
}

function CellHandle() {
  return (
    <PanelResizeHandle className="group relative flex items-center justify-center">
      <div className="w-1 h-full bg-vibe-border group-hover:bg-vibe-accent/50 group-active:bg-vibe-accent transition-colors" />
    </PanelResizeHandle>
  );
}

export interface ModulesGridEntry {
  moduleId: string;
  node: ReactNode;
}

export interface ModulesGridProps {
  tiles: ModulesGridEntry[];
  docked: DockedModule | null;
  maximizedModuleId: string | null;
}

function RowsOfTwo({ tiles, saveId }: { tiles: ModulesGridEntry[]; saveId: string }) {
  if (tiles.length === 0) return null;
  if (tiles.length === 1) {
    return <div className="h-full w-full">{tiles[0].node}</div>;
  }
  const rows = chunk(tiles, ROW_SIZE);
  // react-resizable-panels needs STABLE id + order on every Panel when the set
  // is dynamic (panels added/removed as modules open/close) — without them,
  // adding a 3rd/4th module mid-session leaves the new row collapsed (~14px,
  // below minSize) until a reload, because the library can't map the new panel
  // into the persisted layout. id/order (by position, not membership) + an even
  // defaultSize make a freshly-added row/cell split evenly. Keying the row
  // Fragment by position (not membership) also stops a whole row's iframes from
  // remounting when just one cell is added.
  return (
    <PanelGroup direction="vertical" autoSaveId={saveId}>
      {rows.map((row, rowIdx) => (
        <Fragment key={`row-${rowIdx}`}>
          {rowIdx > 0 && <RowHandle />}
          <Panel
            id={`${saveId}-row-${rowIdx}`}
            order={rowIdx}
            minSize={15}
            defaultSize={100 / rows.length}
          >
            {row.length === 1 ? (
              <div className="h-full w-full">{row[0].node}</div>
            ) : (
              <PanelGroup direction="horizontal" autoSaveId={`${saveId}-row-${rowIdx}`}>
                {row.map((tile, cellIdx) => (
                  <Fragment key={tile.moduleId}>
                    {cellIdx > 0 && <CellHandle />}
                    <Panel
                      id={`${saveId}-row-${rowIdx}-cell-${tile.moduleId}`}
                      order={cellIdx}
                      minSize={15}
                      defaultSize={100 / row.length}
                    >
                      <div className="h-full w-full">{tile.node}</div>
                    </Panel>
                  </Fragment>
                ))}
              </PanelGroup>
            )}
          </Panel>
        </Fragment>
      ))}
    </PanelGroup>
  );
}

export function ModulesGrid({ tiles, docked, maximizedModuleId }: ModulesGridProps) {
  if (tiles.length === 0) return null;

  if (maximizedModuleId) {
    const solo = tiles.find((t) => t.moduleId === maximizedModuleId);
    if (solo) return <div className="h-full w-full">{solo.node}</div>;
  }

  const dockedTile = docked ? tiles.find((t) => t.moduleId === docked.id) : undefined;
  if (docked && dockedTile && tiles.length > 1) {
    const rest = tiles.filter((t) => t.moduleId !== docked.id);
    const axis = docked.edge === 'left' || docked.edge === 'right' ? 'horizontal' : 'vertical';
    const dockedFirst = docked.edge === 'left' || docked.edge === 'top';
    const dockedPanel = (
      <Panel minSize={15} defaultSize={50}>
        <div className="h-full w-full">{dockedTile.node}</div>
      </Panel>
    );
    const restPanel = (
      <Panel minSize={15}>
        <RowsOfTwo tiles={rest} saveId="vibe-modules-grid-rest" />
      </Panel>
    );
    const Handle = axis === 'horizontal' ? CellHandle : RowHandle;
    return (
      <PanelGroup direction={axis} autoSaveId={`vibe-modules-grid-dock-${docked.edge}`}>
        {dockedFirst ? dockedPanel : restPanel}
        <Handle />
        {dockedFirst ? restPanel : dockedPanel}
      </PanelGroup>
    );
  }

  return <RowsOfTwo tiles={tiles} saveId="vibe-modules-grid" />;
}
